import { Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import FaultListSubMenu from './FaultListSubMenu';
import { useNavigate, useParams } from 'react-router-dom';
import { preloadFaultList, useFaultListInfinite } from '../../fetch/fetchFault';
import Spinner from '../spinner/Spinner';
import { useIsInViewport, usePhoneMode } from '../../hooks';
import { useSelector } from 'react-redux';
import { useFamilyNameList } from '../../fetch/fetchClassificationItems';
import FamilySelectModal from './FamilySelectModal';
import FaultRow from './FaultRow';
import { logFaultListView, logFeatureUsage } from '../../fetch/insightsHelper';

export default function FaultList() {
    const { phoneMode, isMobile } = usePhoneMode();
    const theme = useTheme();
    const scrollViewerTop = theme.appBar.height + theme.subBar.height
    const { familyNameId: filteredFamilyNameId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (!!filteredFamilyNameId && filteredFamilyNameId == 'undefined') {
            navigate('/literature/fault');
        }

        if (!filteredFamilyNameId) {
            logFeatureUsage('FaultListNoFamily');
        } else {
            logFeatureUsage('FaultListWithFamily');
        }
    }, [filteredFamilyNameId]);

    const { faultQuery } = useSelector((state) => state.libraryFilters);

    useEffect(() => {
        logFaultListView(faultQuery, filteredFamilyNameId);
    }, [filteredFamilyNameId, faultQuery]);

    const { familyNames: sourceFamilyNames } = useFamilyNameList(true);
    const selectedFamily = sourceFamilyNames.filter(it => it.id == filteredFamilyNameId)[0];

    const [selectedFault, setSelectedFault] = useState(null);
    const showFamilySelect = !!selectedFault && selectedFault.familyNames.length > 0;

    const { faults, size, totalRecords: requestTotalRecords, setSize, isLoading, isValidating } = useFaultListInfinite({ familyNameId: filteredFamilyNameId, query: faultQuery });

    const loadMoreRef = useRef(null);
    const [isInViewport1, _intersectionRatio] = useIsInViewport(loadMoreRef);
    useEffect(() => {
        if (isInViewport1 && !isLoading && !isValidating && faults.length < requestTotalRecords) {
            setTimeout(() => {
                setSize(size + 1);
            }, 250);
        }
    }, [isInViewport1, isLoading, isValidating, faults.length, requestTotalRecords]);

    useEffect(() => {
        if (!isLoading && !isValidating && faults.length < requestTotalRecords) {
            preloadFaultList(size, { filteredFamilyNameId, query: faultQuery });
        }
    }, [size, isLoading, isValidating, faults.length, requestTotalRecords]);

    useEffect(() => {
        if (!selectedFamily) {
            document.title = 'Mercury Literature - Faults';
        } else {
            document.title = `Mercury Literature - Faults for ${selectedFamily.name}`;
        }
    }, [selectedFamily]);

    return (
        <>
            <FaultListSubMenu familyNameId={filteredFamilyNameId} />
            <FamilySelectModal open={showFamilySelect} fault={selectedFault} onClose={() => setSelectedFault(null)} />
            <Grid
                id='scroll-viewer'
                container
                style={{
                    position: 'fixed',
                    top: scrollViewerTop,
                    left: 0,
                    height: `calc(100vh - ${scrollViewerTop}px)`,
                    overflowX: 'hidden',
                    overflowY: 'hidden',
                    width: '100vw',
                    padding: '1.5em 1em 1.5em 1em'
                }}
            >
                <Grid item xs>
                    <Grid container columns={{ xs: 1 }} spacing={2}>
                        <Paper
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                width: 'calc(100% - 1em)',
                                marginLeft: '1em',
                                marginTop: '.5em',
                                marginBottom: '1em',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                position: 'relative'
                            }}
                        >
                            <TableContainer style={{ maxHeight: `calc(100vh - ${scrollViewerTop + (isMobile ? 36 : 0)}px - 3.5em)` }}>
                                <Table stickyHeader size={phoneMode ? "small" : "medium"}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Id</TableCell>
                                            <TableCell>Name</TableCell>
                                            {!!filteredFamilyNameId && !phoneMode ? <TableCell>Family</TableCell> : null}
                                            {!phoneMode ? <TableCell>Description</TableCell> : null}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {!isLoading && requestTotalRecords == 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={!!filteredFamilyNameId ? 4 : 3} align="center">
                                                    <Typography variant="h6">
                                                        No Results
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                        {isLoading && size === 1 ? (
                                            <TableRow>
                                                <TableCell colSpan={!!filteredFamilyNameId ? 4 : 3} align="center">
                                                    <Spinner width={40} />
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                        {faults.map((fault, index) => {
                                            return (
                                                <FaultRow fault={fault} key={index} setSelectedFault={(fault) => setSelectedFault(fault)} />
                                            );
                                        })}
                                        <TableRow style={{ display: faults.length < requestTotalRecords ? null : 'none' }} ref={loadMoreRef}>
                                            <TableCell colSpan={!!filteredFamilyNameId ? 4 : 3} align="center">
                                                {!isLoading && !isValidating ? (
                                                    <Button
                                                        onClick={() => setSize(size + 1)}
                                                    >
                                                        Load Next
                                                        <Spinner width={40} />
                                                    </Button>
                                                ) : (
                                                    <Spinner width={40} />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid >
        </>
    );
}